// This file was moved from ../CreateNode.tsx
import React from 'react';
import { Box, Typography, Paper, TextField, Autocomplete, Button, Divider, Slider, InputAdornment, IconButton, Modal, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AlertTriangle from '../../assets/AlertTriangle.svg';
import { setNodeActive, createNode, updateNode, getCities } from '../../services/nodeService';
import { useState } from 'react';
import ChevronRight from '../../components/ChevronRight';
import Map, { Marker, Source, Layer, type MapRef } from 'react-map-gl/mapbox';
import {
    pageBoxSx,
    pageTitleSx,
    headerWrapperSx,
    breadcrumbRowSx,
    breadcrumbButtonSx,
    breadcrumbSeparatorSx,
    mainColumnSx,
    detailsPaperSx,
    detailsContentSx,
    inputWrapperSx,
    labelSx,
    inputFieldSx,
    addressFieldSx,
    selectSx,
    geofencePaperSx,
    searchBoxSx,
    searchFieldSx,
    mapPlaceholderSx,
    repositionWrapperSx,
    repositionButtonSx,
    coordsBadgeSx,
    radiusContainerSx,
    sliderWrapperSx,
    radiusValueSx,
    staffingPaperSx,
    staffingInputSx,
    statusPaperSx,
    actionRowSx,
    deactivateButtonSx,
    headerTopRowSx,
    activeStatusSx,
    inactiveStatusSx,
    actionRightSx,
    saveButtonSx,
    cancelButtonSx,
    modalContainerSx,
    modalHeaderSx,
    modalTitleSx,
    modalCloseIconSx,
    modalContentSx,
    modalWarningRowSx,
    modalCancelButtonSx,
    modalDeactivateButtonSx,
    modalBodyTextSx,
} from './createNode.styles';
import { sectionTitleSx } from '../../styles/sectionTokens';
import { activeTagSx, inactiveTagSx } from './operatingNodes.styles';

const CreateNode: React.FC<{ onCancel?: () => void; onSaved?: (message?: string) => void; initialNode?: any; onDeactivate?: (nodeName: string) => void }> = ({ onCancel, onSaved, initialNode, onDeactivate }) => {
    const [radius, setRadius] = useState<number>((initialNode?.radius ?? 150) / 1000);
    const [name, setName] = useState<string>(initialNode?.name ?? '');
    const [address, setAddress] = useState<string>(initialNode?.address ?? '');
    // use numeric cityId for payload; dummy ids for now
    const [city, setCity] = useState<number | ''>(initialNode?.cityId ?? '');
    const [minGuards, setMinGuards] = useState<number>(initialNode?.minGuards ?? 1);
    const [coords, setCoords] = useState<{ lat: number; lng: number }>({ lat: initialNode?.latitude ?? 51.51, lng: initialNode?.longitude ?? -0.134 });
    const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN ?? '';
    const isValidMapboxToken = Boolean(mapboxToken && mapboxToken.startsWith('pk.'));
    const mapRef = React.useRef<MapRef | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState<Array<any>>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [citiesList, setCitiesList] = useState<Array<{ id: number; name: string }>>([]);
    const [userEditedAddress, setUserEditedAddress] = useState(false);
    const initialCoordsPresent = Boolean(initialNode?.latitude != null && initialNode?.longitude != null);
    const initialAddress = initialNode?.address ?? '';
    const searchBoxRef = React.useRef<HTMLDivElement | null>(null);
    const [mapAuthError, setMapAuthError] = useState<string | null>(null);
    const [mapChecking, setMapChecking] = useState<boolean>(false);
    // generate a GeoJSON polygon approximating a circle (in meters)
    const createCircleGeoJSON = (center: [number, number], radiusMeters: number, points = 64) => {
        const coords: [number, number][] = [];
        const R = 6378137; // Earth radius in meters (WGS84)
        const [lng, lat] = center;
        for (let i = 0; i < points; i++) {
            const angle = (i * 2 * Math.PI) / points;
            const dx = radiusMeters * Math.cos(angle);
            const dy = radiusMeters * Math.sin(angle);
            const dLat = dy / R;
            const dLng = dx / (R * Math.cos((lat * Math.PI) / 180));
            const pointLat = lat + (dLat * 180) / Math.PI;
            const pointLng = lng + (dLng * 180) / Math.PI;
            coords.push([pointLng, pointLat]);
        }
        // close polygon
        coords.push(coords[0]);
    return {
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [coords],
            },
            properties: {},
    } as any;
    };

    const circleFeature = React.useMemo(() => {
        const center: [number, number] = [coords.lng, coords.lat];
        // radius state is in km; createCircleGeoJSON expects meters
        return createCircleGeoJSON(center, radius * 1000);
    }, [coords.lng, coords.lat, radius]);
    // Only allow the map when a valid Mapbox public token (pk.*) is provided.
    const MAPBOX_STYLE = 'mapbox://styles/mapbox/streets-v11';

    const flyToCoords = React.useCallback((lat: number, lng: number) => {
        try {
            const map = mapRef.current?.getMap && mapRef.current.getMap();
            if (map && typeof map.flyTo === 'function') map.flyTo({ center: [lng, lat], zoom: 13 });
        } catch {}
    }, []);

    // Auto-detect current location for new nodes
    React.useEffect(() => {
        if (initialNode) return; // editing: use existing coords
        const stored = localStorage.getItem('wualt:lastLocation');
        if (stored) {
            try {
                const { lat, lng } = JSON.parse(stored);
                if (typeof lat === 'number' && typeof lng === 'number') {
                    setCoords({ lat, lng });
                    setTimeout(() => flyToCoords(lat, lng), 300);
                    return;
                }
            } catch {}
        }
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const lat = pos.coords.latitude;
                    const lng = pos.coords.longitude;
                    setCoords({ lat, lng });
                    localStorage.setItem('wualt:lastLocation', JSON.stringify({ lat, lng }));
                    setTimeout(() => flyToCoords(lat, lng), 300);
                },
                () => {}
            );
        }
    }, []);

    const handleRadiusChange = (_: Event, value: number | number[]) => {
        const v = Array.isArray(value) ? value[0] : value;
        setRadius(v);
    };

    // Debounced fetching of geocoding suggestions
    React.useEffect(() => {
        if (!isValidMapboxToken) return;
        if (!searchQuery || searchQuery.trim().length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
        const t = setTimeout(async () => {
            try {
                console.debug('geocode: querying', searchQuery);
                const q = encodeURIComponent(searchQuery.trim());
                const host = process.env.REACT_APP_MAPBOX_HOST ?? 'https://api.mapbox.com';
                const url = `${host}/geocoding/v5/mapbox.places/${q}.json?access_token=${mapboxToken}&autocomplete=true&limit=5`;
                const res = await fetch(url);
                if (!res.ok) { console.warn('geocode: non-ok', res.status); setSuggestions([]); setShowSuggestions(true); return; }
                const body = await res.json();
                const feats = Array.isArray(body?.features) ? body.features : [];
                console.debug('geocode: got', feats.length, 'results');
                setSuggestions(feats);
                setShowSuggestions(true);
            } catch (err) {
                console.error('Geocode error', err);
                setSuggestions([]);
                setShowSuggestions(true);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [searchQuery, mapboxToken, isValidMapboxToken]);

    // hide suggestions when clicking outside
    React.useEffect(() => {
        const onDocClick = (ev: MouseEvent) => {
            if (!searchBoxRef.current) return;
            if (!(ev.target instanceof Node)) return;
            if (!searchBoxRef.current.contains(ev.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, []);

    // When the address or city fields change, attempt a geocode and select the location on the map.
    React.useEffect(() => {
        if (!isValidMapboxToken) return;
        // If we're editing an existing node that already has coordinates, do not auto-geocode
        // unless the user has explicitly edited the address/location.
        if (initialCoordsPresent && !userEditedAddress) return;
        const qRaw = `${address || ''} ${typeof city === 'string' ? city : (city ? String(city) : '')}`.trim();
        if (!qRaw || qRaw.length < 3) return;
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const q = encodeURIComponent(qRaw);
                const host = process.env.REACT_APP_MAPBOX_HOST ?? 'https://api.mapbox.com';
                const url = `${host}/geocoding/v5/mapbox.places/${q}.json?access_token=${mapboxToken}&autocomplete=false&limit=1`;
                const res = await fetch(url);
                if (!res.ok) return;
                const body = await res.json().catch(() => null);
                const feat = Array.isArray(body?.features) && body.features[0];
                if (cancelled || !feat) return;
                const [lng, lat] = feat.center || [null, null];
                if (lng != null && lat != null) {
                    setCoords({ lat, lng });
                    try {
                        const map = mapRef.current?.getMap && mapRef.current.getMap();
                        if (map && typeof map.flyTo === 'function') {
                            map.flyTo({ center: [lng, lat], zoom: 15 });
                        }
                    } catch (err) {
                        console.debug('Map centering failed', err);
                    }
                    // try to derive city from feature context and set numeric city id if matched
                    try {
                        const place = feat.place_name || '';
                        const ctx = feat.context || [];
                        let cityText = '';
                        // search context for place or place_name components
                        for (const c of ctx) {
                            if (c?.id?.startsWith('place') || c?.id?.startsWith('locality')) {
                                cityText = c.text || c.place_name || '';
                                break;
                            }
                        }
                        if (!cityText && place) {
                            const parts = String(place).split(',');
                            if (parts.length >= 2) cityText = parts[parts.length - 2].trim();
                        }
                        if (cityText && citiesList.length) {
                            const match = citiesList.find((c) => c.name.toLowerCase() === String(cityText).toLowerCase());
                            if (match) setCity(match.id);
                        }
                    } catch (err) {
                        /* ignore */
                    }
                }
            } catch (err) {
                console.debug('address geocode failed', err);
            }
        }, 500);
        return () => { cancelled = true; clearTimeout(t); };
    }, [address, city, mapboxToken, isValidMapboxToken, mapRef, userEditedAddress, initialCoordsPresent, citiesList]);

    // fetch cities on mount
    React.useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const rows = await getCities();
                if (cancelled) return;
                setCitiesList(Array.isArray(rows) ? rows : []);
            } catch (err) {
                console.debug('Failed to load cities', err);
            }
        };
        load();
        return () => { cancelled = true; };
    }, []);

    // when citiesList becomes available, if we're in edit mode try to set the numeric city id
    React.useEffect(() => {
        if (!initialNode) return;
        if (city) return; // already set
        if (!citiesList || !citiesList.length) return;
        // prefer explicit ids on the initial node
        const nodeCityId = initialNode.city_id ?? initialNode.cityId ?? null;
        if (nodeCityId) {
            setCity(Number(nodeCityId));
            return;
        }
        // fall back to matching by city name
        const nodeCityName = initialNode.city ?? initialNode.city_name ?? '';
        if (nodeCityName) {
            const match = citiesList.find((c) => String(c.name).toLowerCase() === String(nodeCityName).toLowerCase());
            if (match) setCity(match.id);
        }
    }, [citiesList, initialNode]);

    // Preflight check for Mapbox style access to detect invalid/unauthorized tokens (403/401)
    React.useEffect(() => {
        if (!isValidMapboxToken) {
            setMapAuthError(null);
            return;
        }
        let cancelled = false;
        const check = async () => {
            setMapChecking(true);
            try {
                const host = process.env.REACT_APP_MAPBOX_HOST ?? 'https://api.mapbox.com';
                const url = `${host}/styles/v1/mapbox/streets-v11?access_token=${mapboxToken}`;
                const res = await fetch(url, { method: 'GET' });
                if (cancelled) return;
                if (!res.ok) {
                    if (res.status === 401 || res.status === 403) {
                        setMapAuthError('Mapbox token is invalid or unauthorized (HTTP ' + res.status + '). Please provide a valid public token starting with "pk." in your .env file.');
                    } else {
                        setMapAuthError('Failed to load Mapbox style: HTTP ' + res.status);
                    }
                } else {
                    setMapAuthError(null);
                }
            } catch (err) {
                if (cancelled) return;
                console.error('Mapbox preflight check failed', err);
                setMapAuthError('Network error while checking Mapbox token (see console)');
            } finally {
                if (!cancelled) setMapChecking(false);
            }
        };
        check();
        return () => { cancelled = true; };
    }, [mapboxToken, isValidMapboxToken]);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const openConfirm = () => setConfirmOpen(true);
    const closeConfirm = () => setConfirmOpen(false);

    const confirmDeactivate = async () => {
        // call API to deactivate if we have an id
        try {
            const nodeId = initialNode?.id ?? 0;
            const resp = await setNodeActive(nodeId, false);
            if (resp?.success) {
                onDeactivate && onDeactivate(name);
                // notify app via same event used elsewhere
                window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp.message ?? 'Node deactivated' } }));
            } else {
                window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: resp?.message ?? 'Failed to deactivate node' } }));
            }
        } catch (err: any) {
            console.error('Deactivate failed', err);
            window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: err?.message ?? 'Failed to deactivate node' } }));
        } finally {
            closeConfirm();
        }
    };

    return (
        <Box sx={pageBoxSx}>
            <Box sx={{ ...headerWrapperSx, position: 'relative' }}>
                <Box sx={breadcrumbRowSx}>
                    <Button size="small" onClick={() => onCancel && onCancel()} sx={breadcrumbButtonSx}>Node</Button>
                    <Typography sx={breadcrumbSeparatorSx}><ChevronRight /></Typography>
                    <Typography sx={breadcrumbSeparatorSx}>{initialNode ? initialNode.name : 'Create Node'}</Typography>
                </Box>
                <Box sx={headerTopRowSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={pageTitleSx}>{initialNode ? initialNode.name : 'Create node'}</Typography>
                        {initialNode && (
                            <Box component="span" sx={initialNode.status === 'Active' ? activeTagSx : inactiveTagSx}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: initialNode.status === 'Active' ? '#60AF97' : '#9CA3AF' }} />
                                <Box component="span">{initialNode.status}</Box>
                            </Box>
                        )}
                    </Box>
                    <div />
                </Box>
                {formError && (
                    <Box sx={{ position: 'absolute', right: 0, top: 0, width: '356px', height: '54px', borderRadius: '8px', backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5', display: 'flex', alignItems: 'center', px: 2, zIndex: 20 }}>
                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#DE606C', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, flexShrink: 0 }}>
                            <svg width="10" height="10" viewBox="0 0 8 8" fill="none"><path d="M1 1L7 7M7 1L1 7" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" /></svg>
                        </Box>
                        <Typography sx={{ flex: 1, fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '19.5px', letterSpacing: '-0.08px', color: '#374151' }}>{formError}</Typography>
                        <Box component="span" onClick={() => setFormError(null)} sx={{ cursor: 'pointer', color: '#9CA3AF', fontSize: 18, lineHeight: 1, ml: 1 }}>×</Box>
                    </Box>
                )}
            </Box>

            <Box sx={mainColumnSx}>
                <Paper sx={detailsPaperSx}>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Node Details</Typography>
                    <Box sx={detailsContentSx}>
                        <Box sx={inputWrapperSx}>
                            <Typography sx={labelSx}>Name *</Typography>
                            <TextField value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Camden High Street" size="small" fullWidth sx={inputFieldSx} error={Boolean(errors.name)} helperText={errors.name} />
                        </Box>

                        <Box sx={inputWrapperSx}>
                            <Typography sx={labelSx}>Address *</Typography>
                            <TextField value={address} onChange={(e) => { setAddress(e.target.value); setUserEditedAddress(true); }} placeholder="Full address" size="small" fullWidth multiline rows={3} sx={addressFieldSx} error={Boolean(errors.address)} helperText={errors.address} />
                        </Box>

                        <Box sx={inputWrapperSx}>
                            <Typography sx={labelSx}>City *</Typography>
                            <Autocomplete
                                options={citiesList}
                                getOptionLabel={(o) => o.name}
                                value={citiesList.find((c) => c.id === city) ?? null}
                                onChange={(_e, val) => setCity(val?.id ?? '')}
                                size="small"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select city"
                                        sx={selectSx}
                                        error={Boolean(errors.city)}
                                        helperText={errors.city}
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                </Paper>

                <Paper sx={geofencePaperSx}>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Geofence</Typography>
                    <Typography variant="body2" color="text.secondary">Set the GPS location and radius for this node.</Typography>
                    <Box mt={1} display="flex" flexDirection="column" gap={1}>
                        <Box sx={{ ...searchBoxSx, position: 'relative' }} ref={searchBoxRef}>
                            <TextField
                                size="small"
                                placeholder="Search address on map..."
                                fullWidth
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: '#9CA3AF' }} /></InputAdornment>), }}
                                sx={searchFieldSx}
                                onFocus={() => { if (suggestions.length) setShowSuggestions(true); }}
                            />
                            {showSuggestions && suggestions.length > 0 && (
                                <Box sx={{ position: 'absolute', zIndex: 1300, width: '100%', background: '#fff', boxShadow: '0 4px 8px rgba(0,0,0,0.06)', borderRadius: 1, mt: 1 }}>
                                    {suggestions.map((s, i) => (
                                        <Box
                                            key={i}
                                            sx={{ px: 2, py: 1, cursor: 'pointer', '&:hover': { background: '#F9FAFB' } }}
                                                onClick={() => {
                                                    const [lng, lat] = s.center || [null, null];
                                                    if (lng != null && lat != null) {
                                                        // update coords and address fields
                                                        setCoords({ lat, lng });
                                                        setAddress(s.place_name || '');
                                                        // try to derive a city id from the feature context and map to numeric id
                                                        try {
                                                            const ctx = s.context || [];
                                                            let cityText = '';
                                                            for (const c of ctx) {
                                                                if (c?.id?.startsWith('place') || c?.id?.startsWith('locality')) {
                                                                    cityText = c.text || '';
                                                                    break;
                                                                }
                                                            }
                                                            if (!cityText && s.place_name) {
                                                                const parts = String(s.place_name).split(',');
                                                                if (parts.length >= 2) cityText = parts[parts.length - 2].trim();
                                                            }
                                                            if (cityText && citiesList.length) {
                                                                const match = citiesList.find((c) => c.name.toLowerCase() === String(cityText).toLowerCase());
                                                                if (match) setCity(match.id);
                                                                else setCity('');
                                                            } else {
                                                                setCity('');
                                                            }
                                                        } catch (err) {
                                                            // fallback to empty value
                                                            setCity('');
                                                        }
                                                        // if the map is mounted, animate/center to the selected location
                                                        try {
                                                            const map = mapRef.current?.getMap && mapRef.current.getMap();
                                                            if (map && typeof map.flyTo === 'function') {
                                                                map.flyTo({ center: [lng, lat], zoom: 15 });
                                                            }
                                                        } catch (err) {
                                                            console.debug('Map centering failed', err);
                                                        }
                                                        // mark that the user intentionally changed the address/location
                                                        setUserEditedAddress(true);
                                                    }
                                                    setSearchQuery(s.place_name || '');
                                                    setShowSuggestions(false);
                                                }}
                                        >
                                            {s.place_name}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ height: 300, position: 'relative' }}>
                            {isValidMapboxToken && !mapAuthError ? (
                                <Map
                                    ref={mapRef}
                                    initialViewState={{ latitude: coords.lat, longitude: coords.lng, zoom: 12 }}
                                    style={{ width: '100%', height: 300 }}
                                    mapStyle={MAPBOX_STYLE}
                                    mapboxAccessToken={mapboxToken}
                                    onClick={(evt: any) => {
                                        const [lng, lat] = [evt.lngLat.lng, evt.lngLat.lat];
                                        setCoords({ lat, lng });
                                        setUserEditedAddress(true);
                                    }}
                                >
                                {circleFeature && (
                                    <Source id="node-circle" type="geojson" data={circleFeature}>
                                        <Layer id="node-circle-fill" type="fill" paint={{ 'fill-color': '#2DB8A6', 'fill-opacity': 0.12 }} />
                                    </Source>
                                )}
                                <Marker longitude={coords.lng} latitude={coords.lat} anchor="bottom">
                                    <Box sx={{ width: 24, height: 24, backgroundColor: '#2DB8A6', borderRadius: '50%', border: '2px solid #fff' }} />
                                </Marker>
                                </Map>
                            ) : (
                                <Box sx={mapPlaceholderSx}>
                                    <Box sx={repositionWrapperSx}>
                                        <Typography variant="body2" color="text.secondary">
                                            {mapAuthError ? (
                                                <>
                                                    {mapAuthError}
                                                    {mapChecking && (
                                                        <Box component="span" sx={{ ml: 1 }}><CircularProgress size={14} /></Box>
                                                    )}
                                                </>
                                            ) : (
                                                'Map disabled: REACT_APP_MAPBOX_TOKEN is missing or not a public token (must start with "pk.").'
                                            )}
                                        </Typography>
                                    </Box>
                                    <Box sx={coordsBadgeSx}>
                                        <Typography variant="caption">{coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}</Typography>
                                    </Box>
                                </Box>
                            )}
                            <Box sx={coordsBadgeSx}>
                                <Typography variant="caption">{coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}</Typography>
                            </Box>
                        </Box>

                        <Box sx={radiusContainerSx}>
                            <Typography variant="caption">Geofence radius (km)</Typography>
                            <Box>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Typography variant="body2">0.05 km</Typography>
                                    <Box sx={sliderWrapperSx}>
                                        <Slider min={0.05} max={100} step={0.05} value={Math.min(100, Math.max(0.05, radius || 0.05))} aria-label="geofence-radius" onChange={handleRadiusChange} sx={{ mt: 0 }} />
                                    </Box>
                                    <Typography variant="body2">100 km</Typography>
                                </Box>
                                <Box display="flex" justifyContent="flex-end" mt={0.5}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={radius}
                                        onChange={(e) => setRadius(Number(e.target.value))}
                                        slotProps={{ htmlInput: { step: 0.05 }, input: { endAdornment: <InputAdornment position="end"><Typography variant="caption">km</Typography></InputAdornment> } }}
                                        sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: '8px', height: 34 }, '& input': { textAlign: 'center', px: 1 } }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        {errors.radius && <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>{errors.radius}</Typography>}
                    </Box>
                </Paper>

                <Paper sx={staffingPaperSx}>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Staffing</Typography>
                        <Box>
                        <Typography sx={labelSx}>Minimum guard count</Typography>
                        <TextField type="number" value={minGuards} onChange={(e) => setMinGuards(Number(e.target.value))} size="small" defaultValue={1} sx={staffingInputSx} error={Boolean(errors.minGuards)} helperText={errors.minGuards} />
                    </Box>
                </Paper>

                <Box sx={actionRowSx}>
                    {initialNode ? (
                        <Button variant="text" sx={deactivateButtonSx} onClick={openConfirm}>Deactivate Node</Button>
                    ) : <div />}
                        <Box sx={actionRightSx}>
                        <Button variant="contained" sx={saveButtonSx} onClick={async () => {
                             // clear previous errors
                             setErrors({});
                             const e: Record<string, string> = {};
                             if (!name || name.trim().length < 2) e.name = 'Name is required and should be at least 2 characters';
                             if (!address || address.trim().length < 5) e.address = 'Address is required';
                            if (!city) e.city = 'City is required';
                             if (!coords || typeof coords.lat !== 'number' || typeof coords.lng !== 'number') e.coords = 'Select a location on the map';
                             if (!minGuards || minGuards < 1) e.minGuards = 'Minimum guard count must be at least 1';
                             if (!radius || radius < 0.05 || radius > 100) e.radius = 'Radius must be between 0.05 km and 100 km';
                             if (Object.keys(e).length) { setErrors(e); return; }

                            // create payload matching API spec
                            const payload = {
                                name: name.trim(),
                                address: address.trim(),
                                latitude: Number(coords.lat),
                                longitude: Number(coords.lng),
                                geofence_radius_m: Math.round(radius * 1000),
                                minimum_guard_count: Number(minGuards),
                                status: 'live',
                                city_id: Number(city) || 1, // TODO: replace with real city id values
                            };

                            if (!initialNode) {
                                setSaving(true);
                                try {
                                    const resp = await createNode(payload);
                                    if (resp?.success) {
                                        window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp.message ?? 'Node created' } }));
                                        onSaved && onSaved('Node created');
                                    } else {
                                        setFormError(resp?.message ?? 'Failed to create node');
                                    }
                                } catch (err: any) {
                                    console.error('Create node failed', err);
                                    setFormError(err?.message ?? 'Create node failed');
                                } finally {
                                    setSaving(false);
                                }
                            } else {
                                // Edit flow: compute patch with changed fields and call updateNode
                                const nodeId = initialNode.id;
                                const patch: Record<string, any> = {};
                                if (name.trim() !== (initialNode.name ?? '')) patch.name = name.trim();
                                if (address.trim() !== (initialNode.address ?? '')) patch.address = address.trim();
                                if (Number(coords.lat) !== Number(initialNode.latitude)) patch.latitude = Number(coords.lat);
                                if (Number(coords.lng) !== Number(initialNode.longitude)) patch.longitude = Number(coords.lng);
                                const initialRadius = Number(initialNode.geofence_radius_m ?? initialNode.radius ?? 150) / 1000;
                                if (radius !== initialRadius) patch.geofence_radius_m = Math.round(radius * 1000);
                                const initialMin = Number(initialNode.minimum_guard_count ?? initialNode.minGuards ?? 1);
                                if (Number(minGuards) !== initialMin) patch.minimum_guard_count = Number(minGuards);
                                // support both snake_case and camelCase on initialNode
                                const initialCityId = initialNode.city_id ?? initialNode.cityId ?? initialNode.city ?? null;
                                if (Number(city) !== Number(initialCityId)) patch.city_id = Number(city);
                                // only send status if it is different (for now default live)
                                if ((initialNode.status ?? 'live') !== 'live') patch.status = 'live';
                                if (Object.keys(patch).length === 0) { onSaved && onSaved('No changes'); return; }
                                setSaving(true);
                                try {
                                    const resp = await updateNode(nodeId, patch);
                                    if (resp?.success) {
                                        window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp.message ?? 'Node updated' } }));
                                        onSaved && onSaved('Node updated');
                                    } else {
                                        setFormError(resp?.message ?? 'Failed to update node');
                                    }
                                } catch (err: any) {
                                    console.error('Update node failed', err);
                                    setFormError(err?.message ?? 'Update node failed');
                                } finally {
                                    setSaving(false);
                                }
                            }
                        }}>
                            {saving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button variant="contained" sx={cancelButtonSx} onClick={onCancel}>Cancel</Button>
                    </Box>
                </Box>
                {/* Deactivate confirmation modal */}
                <Modal open={confirmOpen} onClose={closeConfirm}>
                    <Box sx={modalContainerSx}>
                        <Box sx={modalHeaderSx}>
                            <Typography sx={modalTitleSx}>Deactivate node?</Typography>
                            <IconButton size="small" onClick={closeConfirm} sx={modalCloseIconSx}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </IconButton>
                        </Box>
                        <Box sx={modalContentSx}>
                            <Box sx={modalWarningRowSx}>
                                <Box sx={{
                                    width: '80px',
                                    height: '36px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '50%',
                                    backgroundColor: '#FEF2F2',
                                    opacity: 1,
                                    boxSizing: 'border-box',
                                    overflow: 'hidden',
                                    p: 0,
                                }}>
                                    <Box component="img" src={AlertTriangle} sx={{ width: '16px', height: '16px', opacity: 1, display: 'block', objectFit: 'contain' }} />
                                </Box>
                                <Box>
                                    <Typography sx={modalBodyTextSx}>Deactivated nodes are hidden from guard check-in options. Guards assigned to this node will not be affected.</Typography>
                                </Box>
                            </Box>
                            <Box display="flex" justifyContent="flex-end" gap={2}>
                                <Button sx={modalCancelButtonSx} onClick={closeConfirm}>Cancel</Button>
                                <Button sx={modalDeactivateButtonSx} onClick={() => { confirmDeactivate(); }}>Deactivate</Button>
                            </Box>
                        </Box>
                    </Box>
                </Modal>
            </Box>
        </Box>
    );
};

export default CreateNode;

// Deactivate confirmation modal JSX placed after component export (no render impact)

